import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { FaCheck, FaStar } from 'react-icons/fa';
import { FiChevronDown } from 'react-icons/fi';
import { PRICING } from '../config/content';
import styles from './Pricing.module.css';
import SEO from '../components/SEO';

const Reveal: React.FC<{
  children: React.ReactNode;
  className?: string;
}> = ({ children, className }) => {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
};

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut' as const },
  },
};

const Pricing: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <main className={styles.page}>
      <SEO path="/pricing" />
      {/* Ambient glows */}
      <div className={`${styles.ambient} ${styles.ambient1}`} />
      <div className={`${styles.ambient} ${styles.ambient2}`} />

      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <div className={styles.heroContent}>
          <motion.span
            className={styles.heroLabel}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            {PRICING.hero.label}
          </motion.span>
          <motion.h1
            className={styles.heroTitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.7 }}
          >
            {PRICING.hero.title}
          </motion.h1>
          <motion.p
            className={styles.heroSubtitle}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.7 }}
          >
            {PRICING.hero.subtitle}
          </motion.p>
        </div>
        <div className={styles.heroFade} />
      </div>

      <div className={styles.sectionDivider} />

      {/* ── Pricing Cards ── */}
      <section className={styles.pricingSection}>
        <motion.div
          className={styles.cardsGrid}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {PRICING.plans.map((plan, index) => (
            <motion.div
              key={index}
              className={`${styles.card} ${plan.featured ? styles.cardFeatured : ''}`}
              variants={cardVariants}
            >
              {plan.featured && (
                <div className={styles.popularBadge}>
                  <FaStar size={10} />
                  {PRICING.featuredBadge}
                </div>
              )}

              <div className={styles.cardHeader}>
                <h3 className={styles.planName}>{plan.name}</h3>
                <p className={styles.planDescription}>{plan.description}</p>
                {plan.currency ? (
                  <div className={styles.priceWrapper}>
                    <span className={styles.priceCurrency}>{plan.currency}</span>
                    <span className={styles.priceAmount}>{plan.price}</span>
                    <span className={styles.pricePeriod}>{plan.period}</span>
                  </div>
                ) : (
                  <div className={styles.priceWrapper}>
                    <span className={styles.priceCustom}>{plan.price}</span>
                  </div>
                )}
              </div>

              <ul className={styles.featuresList}>
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className={styles.featureItem}>
                    <FaCheck className={styles.featureIcon} />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              {plan.ctaLink.startsWith('http') ? (
                <a
                  href={plan.ctaLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`${styles.ctaButton} ${plan.featured ? styles.ctaButtonPrimary : ''}`}
                >
                  {plan.cta}
                </a>
              ) : (
                <Link
                  to={plan.ctaLink}
                  className={`${styles.ctaButton} ${plan.featured ? styles.ctaButtonPrimary : ''}`}
                >
                  {plan.cta}
                </Link>
              )}
            </motion.div>
          ))}
        </motion.div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── Plan Management Guide ── */}
      <section className={styles.guideSection}>
        <Reveal className={styles.guideHeader}>
          <p className={styles.guideLabel}>{PRICING.guide.label}</p>
          <h2 className={styles.guideTitle}>{PRICING.guide.title}</h2>
          <p className={styles.guideSubtitle}>{PRICING.guide.subtitle}</p>
        </Reveal>

        <Reveal className={styles.guideTableWrapper}>
          <div className={styles.guideTableScroll}>
            <table className={styles.guideTable}>
              <thead>
                <tr>
                  {PRICING.guide.table.headers.map((header, hIndex) => (
                    <th key={hIndex}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PRICING.guide.table.rows.map((row, rIndex) => (
                  <tr key={rIndex}>
                    <td data-label={PRICING.guide.table.headers[0]}>
                      <span className={styles.guidePlanFrom}>{row.from}</span>
                    </td>
                    <td data-label={PRICING.guide.table.headers[1]}>
                      <span className={styles.guidePlanTo}>{row.to}</span>
                    </td>
                    <td data-label={PRICING.guide.table.headers[2]}>
                      {row.charged}
                    </td>
                    <td data-label={PRICING.guide.table.headers[3]}>
                      {row.how}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Reveal>

        <motion.div
          className={styles.guideGrid}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-50px' }}
        >
          {PRICING.guide.sections.map((section, index) => (
            <motion.div
              key={index}
              className={styles.guideCard}
              variants={cardVariants}
            >
              <div className={styles.guideCardIndex}>
                {String(index + 1).padStart(2, '0')}
              </div>
              <h3 className={styles.guideCardTitle}>{section.title}</h3>
              <ul className={styles.guideCardList}>
                {section.points.map((point, pIndex) => (
                  <li key={pIndex} className={styles.guideCardItem}>
                    <FaCheck className={styles.guideCardIcon} />
                    <span>{point}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </motion.div>
      </section>

      <div className={styles.sectionDivider} />

      {/* ── FAQ ── */}
      <section className={styles.faqSection}>
        <Reveal className={styles.faqHeader}>
          <p className={styles.faqLabel}>{PRICING.faq.label}</p>
          <h2 className={styles.faqTitle}>{PRICING.faq.title}</h2>
          <p className={styles.faqSubtitle}>{PRICING.faq.subtitle}</p>
        </Reveal>

        <div className={styles.faqList}>
          {PRICING.faq.items.map((faq, index) => (
            <motion.div
              key={index}
              className={styles.faqItem}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.05 }}
            >
              <button
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaq === index}
              >
                <span>{faq.question}</span>
                <FiChevronDown
                  className={`${styles.faqChevron} ${
                    openFaq === index ? styles.faqChevronOpen : ''
                  }`}
                />
              </button>
              <AnimatePresence initial={false}>
                {openFaq === index && (
                  <motion.div
                    className={styles.faqAnswer}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' as const }}
                  >
                    <div className={styles.faqAnswerInner}>{faq.answer}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <div className={styles.ctaBanner}>
        <Reveal className={styles.ctaBannerContent}>
          <h2 className={styles.ctaBannerTitle}>{PRICING.cta.title}</h2>
          <p className={styles.ctaBannerText}>{PRICING.cta.subtitle}</p>
          {PRICING.cta.buttonLink.startsWith('http') ? (
            <a
              href={PRICING.cta.buttonLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.ctaBannerButton}
            >
              {PRICING.cta.button}
            </a>
          ) : (
            <Link to={PRICING.cta.buttonLink} className={styles.ctaBannerButton}>
              {PRICING.cta.button}
            </Link>
          )}
        </Reveal>
      </div>
    </main>
  );
};

export default Pricing;
